// =============================================================================
// client/src/components/DateTriad.jsx | v1.0 | SQ-S1 | Month / Day / Year selects
// =============================================================================
// Jake's spec for the Call Sheet (cw_003 ruling 5): dates on the profile tab
// and in the completion modal are set with three dropdowns, not the native
// date input the rest of the app uses. Convention noted; product wins.
//
//   <DateTriad value="2026-09-01" onChange={(iso) => …} />
//
// · value / onChange speak 'YYYY-MM-DD' (or '' when incomplete).
// · The day list clamps to the selected month + year (Feb 29 only on leap
//   years). Changing month/year with a now-invalid day snaps the day down to
//   the last valid one, so the emitted value is always a real date.
// · onChange fires ONLY when all three parts are set — a half-picked date is
//   never emitted, so a PATCH-on-change caller cannot send garbage.
// · Year range defaults 2024–2035 (cw_003); extended automatically to include
//   an out-of-range incoming value so an old row still displays honestly.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function parse(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return { y: '', m: '', d: '' };
  const [y, m, d] = value.split('-').map(Number);
  return { y, m, d };
}

function pad(n) { return String(n).padStart(2, '0'); }

export default function DateTriad({
  value = '',
  onChange,
  minYear = 2024,
  maxYear = 2035,
  disabled = false,
  compact = true,
  ariaLabel = 'Date',
}) {
  const [parts, setParts] = useState(() => parse(value));

  // Re-seed when the caller's value changes (a refetch, a server-corrected value).
  useEffect(() => { setParts(parse(value)); }, [value]);

  const years = useMemo(() => {
    const lo = Math.min(minYear, parts.y || minYear);
    const hi = Math.max(maxYear, parts.y || maxYear);
    const out = [];
    for (let y = lo; y <= hi; y++) out.push(y);
    return out;
  }, [minYear, maxYear, parts.y]);

  const dayCount = daysInMonth(parts.y, parts.m);
  const days = useMemo(() => Array.from({ length: dayCount }, (_, i) => i + 1), [dayCount]);

  function commit(next) {
    // Clamp the day to the month/year it now lives in.
    if (next.y && next.m && next.d) {
      const max = daysInMonth(next.y, next.m);
      if (next.d > max) next = { ...next, d: max };
    }
    setParts(next);
    if (next.y && next.m && next.d && onChange) {
      const iso = `${next.y}-${pad(next.m)}-${pad(next.d)}`;
      if (iso !== value) onChange(iso);
    }
  }

  const sel = `input ${compact ? 'text-sm py-1 px-2' : ''} disabled:opacity-50`;

  return (
    <span className="inline-flex items-center gap-1" role="group" aria-label={ariaLabel}>
      <select
        className={`${sel} w-[4.6rem]`}
        value={parts.m || ''}
        disabled={disabled}
        aria-label={`${ariaLabel} month`}
        onChange={(e) => commit({ ...parts, m: Number(e.target.value) || '' })}
      >
        <option value="">Mon</option>
        {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
      </select>
      <select
        className={`${sel} w-[3.9rem]`}
        value={parts.d || ''}
        disabled={disabled}
        aria-label={`${ariaLabel} day`}
        onChange={(e) => commit({ ...parts, d: Number(e.target.value) || '' })}
      >
        <option value="">Day</option>
        {days.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <select
        className={`${sel} w-[4.8rem]`}
        value={parts.y || ''}
        disabled={disabled}
        aria-label={`${ariaLabel} year`}
        onChange={(e) => commit({ ...parts, y: Number(e.target.value) || '' })}
      >
        <option value="">Year</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </span>
  );
}
